/**
 * The SPA boot gate and the backend health probe are the same query (§11.2, §12.1).
 *
 * `GET /api/auth/me` answers three ways: `200` (authenticated), `401` (go to /login) and
 * "no answer at all" (backend unreachable → global banner). It re-runs on an interval and on
 * window focus, so the banner clears by itself once the server is back.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { AuthMeResponse } from '@lw/shared';
import { getAuthMe } from '../api/auth';
import { QK } from '../api/queryKeys';
import { isUnauthorizedError } from '../api/client';

/** Health-probe cadence (§11.2) — cheap, and the only thing that clears the banner. */
export const HEALTH_PROBE_INTERVAL_MS = 30_000;

export type AuthState = {
  query: UseQueryResult<AuthMeResponse, Error>;
  /** Boot probe still in flight and nothing cached yet. */
  isBooting: boolean;
  authenticated: boolean;
  /** `401` — a definitive answer, not a failure. */
  isUnauthenticated: boolean;
  /** Any non-401 failure of the probe: network error or 5xx. */
  isBackendUnreachable: boolean;
};

export function useAuth(): AuthState {
  const query = useQuery({
    queryKey: QK.auth,
    queryFn: ({ signal }) => getAuthMe(signal),
    refetchInterval: HEALTH_PROBE_INTERVAL_MS,
    staleTime: 0,
  });

  const unauthorized = query.isError && isUnauthorizedError(query.error);

  return {
    query,
    isBooting: query.isPending,
    authenticated: query.data?.authenticated === true,
    isUnauthenticated: unauthorized,
    isBackendUnreachable: query.isError && !unauthorized,
  };
}
